import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Send, Building, Phone, Mail, Info, MoreVertical, Check, Image as ImageIcon, Loader } from 'lucide-react';
import { StockPost } from './StockSharingFeed';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { logger } from '../services/logger.service';
import { imageUploadAPI } from '../lib/image-upload-api';

interface PropertyChatProps {
  post: StockPost;
  onClose: () => void;
  onSendMessage: (postId: string, message: string, userId: string, userName: string, userRole: string, imageUrl?: string | null) => void;
  onExpressInterest: (postId: string, userId: string, userName: string, userRole: string) => void;
  onUpdateStatus: (postId: string, status: 'SOLD' | 'BOUGHT' | 'WITHDRAWN') => void;
}

export function PropertyChat({ post, onClose, onSendMessage, onExpressInterest, onUpdateStatus }: PropertyChatProps) {
  const [message, setMessage] = useState('');
  const [showAgentProfile, setShowAgentProfile] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  
  // Get current user from auth service
  const currentUser = authService.getCurrentUser();
  const currentUserName = currentUser?.name || 'Guest User';
  const currentUserId = currentUser?.id || 'guest';
  const currentUserRole = currentUser?.role || 'guest';
  
  // Prefer authorId (Cognito sub) for ownership check; fall back to name comparison
  const isOwnPost = post.authorId
    ? post.authorId === currentUserId
    : post.agentName === currentUserName;
  // Admin can always manage any post (mark sold, withdraw)
  const canManagePost = isOwnPost || currentUserRole === 'admin';

  // Debug log
  useEffect(() => {
    console.log('PropertyChat mounted with post:', post);
    console.log('Current user:', { currentUserId, currentUserName, currentUserRole });
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [post.messages]);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setIsUploadingImage(true);

    try {
      // Use the existing image upload API
      const { url } = await imageUploadAPI.uploadImage(file);
      setSelectedImage(url);
      toast.success('Image uploaded successfully');
    } catch (error) {
      logger.error('Error uploading image', error as Error, undefined, 'PropertyChat');
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setIsUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
  };

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if ((!message.trim() && !selectedImage) || post.status !== 'ACTIVE') return;

    onSendMessage(post.id, message || '📷 Image', currentUserId, currentUserName, currentUserRole, selectedImage);
    setMessage('');
    setSelectedImage(null);
  };

  const handleExpressInterest = () => {
    if (post.status !== 'ACTIVE') return;
    
    const hasAlreadyExpressedInterest = post.messages.some(
      msg => msg.isInterest && msg.text.includes(currentUserName)
    );
    
    if (!hasAlreadyExpressedInterest) {
      onExpressInterest(post.id, currentUserId, currentUserName, currentUserRole);
    }
  };

  const hasExpressedInterest = post.messages.some(
    msg => msg.isInterest && msg.text.includes(currentUserName)
  );

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  return (
    <AnimatePresence>
      <>
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
        />

        {/* Chat Panel */}
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="w-full max-w-4xl h-[85vh] bg-[#1a1a1a] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-[#0a0a0a] border-b border-white/10 p-4 flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className={`px-3 py-1 text-xs font-bold rounded-full ${
                      post.type === 'BUY'
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                        : 'bg-green-500/20 text-green-400 border border-green-500/30'
                    }`}
                  >
                    {post.type}
                  </span>
                  {post.status !== 'ACTIVE' && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-white/10 border border-white/20">
                      {post.status}
                    </span>
                  )}
                </div>
                <h3 className="font-bold text-lg truncate">{post.propertyTitle}</h3>
                <p className="text-sm text-white/60">
                  Posted by {post.agentName} • {post.agency}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAgentProfile(!showAgentProfile)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                >
                  <Info className="w-5 h-5" />
                </button>

                {canManagePost && post.status === 'ACTIVE' && (
                  <div className="relative">
                    <button
                      onClick={() => setShowStatusMenu(!showStatusMenu)}
                      className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>

                    <AnimatePresence>
                      {showStatusMenu && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: -10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: -10 }}
                          className="absolute right-0 top-12 w-48 bg-[#2a2a2a] border border-white/10 rounded-xl shadow-xl overflow-hidden z-10"
                        >
                          <button
                            onClick={() => {
                              onUpdateStatus(post.id, post.type === 'SELL' ? 'SOLD' : 'BOUGHT');
                              setShowStatusMenu(false);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center gap-2"
                            data-testid="property-chat-mark-sold-button"
                          >
                            <Check className="w-4 h-4 text-green-400" />
                            <span>Mark as {post.type === 'SELL' ? 'SOLD' : 'BOUGHT'}</span>
                          </button>
                          <button
                            onClick={() => {
                              onUpdateStatus(post.id, 'WITHDRAWN');
                              setShowStatusMenu(false);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center gap-2"
                            data-testid="property-chat-withdraw-button"
                          >
                            <X className="w-4 h-4 text-red-400" />
                            <span>Withdraw Listing</span>
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                <button
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
              {/* Chat Area */}
              <div className="flex-1 flex flex-col">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {post.messages.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
                        <Send className="w-8 h-8 text-white/20" />
                      </div>
                      <p className="text-white/60">No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    post.messages.map((msg) => (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={
                          msg.isSystem
                            ? 'flex justify-center'
                            : msg.userId === currentUserId
                            ? 'flex items-end justify-end gap-2'
                            : 'flex items-end justify-start gap-2'
                        }
                      >
                        {msg.isSystem ? (
                          <div className={`px-4 py-2 rounded-lg text-sm ${
                            msg.isInterest
                              ? 'bg-[#FDB022]/20 text-[#FDB022] border border-[#FDB022]/30'
                              : 'bg-white/5 text-white/60 border border-white/10'
                          }`}>
                            {msg.text}
                          </div>
                        ) : (
                          <>
                            {/* Avatar for received messages — left side */}
                            {msg.userId !== currentUserId && (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FDB022] to-[#FDB022]/60 flex items-center justify-center flex-shrink-0 mb-1">
                                <span className="text-black text-xs font-bold">
                                  {(msg.userName || 'U').charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}

                            <div className="max-w-[70%]">
                              <div className={`rounded-2xl px-4 py-3 ${
                                msg.userId === currentUserId
                                  ? 'bg-[#FDB022] text-black rounded-br-sm'
                                  : 'bg-white/10 text-white rounded-bl-sm'
                              }`}>
                                {/* Sender name — shown for received messages */}
                                {msg.userId !== currentUserId && (
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-xs font-bold text-[#FDB022]">
                                      {msg.userName || 'Unknown Agent'}
                                    </p>
                                    {msg.userRole && (
                                      <span className="text-xs px-2 py-0.5 bg-white/10 rounded-full opacity-70">
                                        {msg.userRole === 'agent' ? 'Agent' :
                                         msg.userRole === 'external-agent' ? 'Ext. Agent' :
                                         msg.userRole === 'admin' ? 'Admin' :
                                         msg.userRole === 'seller' ? 'Seller' : msg.userRole}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {msg.image && (
                                  <img
                                    src={msg.image}
                                    alt="Shared"
                                    className="rounded-lg mb-2 max-w-full cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => window.open(msg.image, '_blank')}
                                  />
                                )}
                                {msg.text && <p>{msg.text}</p>}
                              </div>
                              <p className={`text-xs text-white/40 mt-1 ${
                                msg.userId === currentUserId ? 'text-right' : 'ml-1'
                              }`}>
                                {msg.userId === currentUserId ? 'You' : (msg.userName || 'Unknown')} · {formatTime(msg.timestamp)}
                              </p>
                            </div>

                            {/* Avatar for sent messages — right side */}
                            {msg.userId === currentUserId && (
                              <div className="w-8 h-8 rounded-full bg-[#FDB022] flex items-center justify-center flex-shrink-0 mb-1">
                                <span className="text-black text-xs font-bold">
                                  {(currentUserName || 'U').charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </motion.div>
                    ))
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Input Area */}
                {post.status === 'ACTIVE' ? (
                  <div className="border-t border-white/10 p-4">
                    {/* Image Preview */}
                    {selectedImage && (
                      <div className="mb-3 relative inline-block">
                        <img 
                          src={selectedImage} 
                          alt="Preview" 
                          className="max-h-32 rounded-lg border border-white/10"
                        />
                        <button
                          onClick={handleRemoveImage}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
                          data-testid="property-chat-remove-image-button"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    <form onSubmit={handleSend} className="flex gap-3">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageSelect}
                        accept="image/*"
                        className="hidden"
                      />
                      
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploadingImage}
                        className="px-4 py-3 bg-white/5 hover:bg-white/10 disabled:bg-white/5 disabled:opacity-50 border border-white/10 rounded-xl transition-all flex items-center gap-2"
                        data-testid="property-chat-image-upload-button"
                      >
                        {isUploadingImage ? (
                          <Loader className="w-5 h-5 animate-spin" />
                        ) : (
                          <ImageIcon className="w-5 h-5" />
                        )}
                      </button>

                      <input
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Discuss this property..."
                        className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-[#FDB022]/50 focus:bg-white/10 transition-all"
                      />
                      
                      <motion.button
                        type="submit"
                        disabled={(!message.trim() && !selectedImage) || isUploadingImage}
                        className="px-6 py-3 bg-[#FDB022] hover:bg-[#FDB022]/90 disabled:bg-white/10 disabled:text-white/40 text-black rounded-xl font-medium transition-all flex items-center gap-2"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        data-testid="property-chat-send-button"
                      >
                        <Send className="w-4 h-4" />
                        Send
                      </motion.button>
                    </form>
                  </div>
                ) : (
                  <div className="border-t border-white/10 p-4 bg-white/5">
                    <p className="text-center text-white/60 text-sm">
                      This listing is no longer active. Chat is now read-only.
                    </p>
                  </div>
                )}
              </div>

              {/* Agent Profile Sidebar */}
              <AnimatePresence>
                {showAgentProfile && (
                  <motion.div
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 320, opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    className="border-l border-white/10 bg-[#0a0a0a] overflow-hidden"
                  >
                    <div className="p-6 space-y-6 w-80">
                      <div>
                        <h3 className="font-bold mb-4">Property Details</h3>
                        <div className="space-y-3 text-sm">
                          <div>
                            <p className="text-white/60 mb-1">Type</p>
                            <p className="font-medium">{post.propertyType}</p>
                          </div>
                          <div>
                            <p className="text-white/60 mb-1">Location</p>
                            <p className="font-medium">{post.location}</p>
                          </div>
                          <div>
                            <p className="text-white/60 mb-1">Price</p>
                            <p className="font-medium text-[#FDB022]">{post.price}</p>
                          </div>
                          {post.bedrooms && (
                            <div>
                              <p className="text-white/60 mb-1">Bedrooms</p>
                              <p className="font-medium">{post.bedrooms}</p>
                            </div>
                          )}
                          {post.bathrooms && (
                            <div>
                              <p className="text-white/60 mb-1">Bathrooms</p>
                              <p className="font-medium">{post.bathrooms}</p>
                            </div>
                          )}
                          {post.size && (
                            <div>
                              <p className="text-white/60 mb-1">Size</p>
                              <p className="font-medium">{post.size} m²</p>
                            </div>
                          )}
                          <div>
                            <p className="text-white/60 mb-1">Description</p>
                            <p className="font-medium leading-relaxed">{post.description}</p>
                          </div>
                        </div>
                      </div>

                      <div className="pt-6 border-t border-white/10">
                        <h3 className="font-bold mb-4">Agent Contact</h3>
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 rounded-full bg-[#FDB022]/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-[#FDB022] font-bold">
                                {(post.agentName || 'U').split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium">{post.agentName}</p>
                              <div className="flex items-center gap-2 text-sm text-white/60 mt-1">
                                <Building className="w-3 h-3" />
                                {post.agency}
                              </div>
                            </div>
                          </div>

                          <div className="space-y-3 text-sm">
                            <a
                              href={`tel:+27123456789`}
                              className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-all"
                            >
                              <Phone className="w-4 h-4 text-[#FDB022]" />
                              <div>
                                <p className="text-white/60 text-xs">Phone</p>
                                <p className="font-medium">+27 12 345 6789</p>
                              </div>
                            </a>

                            <a
                              href={`mailto:${(post.agentName || '').toLowerCase().replace(' ', '.')}@example.com`}
                              className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-all"
                            >
                              <Mail className="w-4 h-4 text-[#FDB022]" />
                              <div>
                                <p className="text-white/60 text-xs">Email</p>
                                <p className="font-medium text-xs">
                                  {(post.agentName || '').toLowerCase().replace(' ', '.')}@example.com
                                </p>
                              </div>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </>
    </AnimatePresence>
  );
}
